/**
 * Admin Access Utility - Simplified Version
 * Full-featured admin system with hardcoded admin users
 */

import { supabase } from '../lib/supabase';

export interface AdminUser {
  email: string;
  phone?: string;
  name: string;
  role: 'admin' | 'super_admin';
  permissions: string[];
  defaultPassword: string;
}

// Define admin users - YOU CAN MODIFY THIS LIST
const ADMIN_USERS: AdminUser[] = [
  {
    email: 'admin@gograbjob.com',
    phone: '+916361235704',
    name: 'System Admin',
    role: 'super_admin',
    permissions: ['all'],
    defaultPassword: 'admin123'
  },
  {
    email: 'support@gograbjob.com',
    name: 'Support Admin',
    role: 'admin',
    permissions: ['user_management', 'fraud_monitoring', 'analytics_view', 'session_management'],
    defaultPassword: 'support123'
  },
  {
    email: 'dharmendra@example.com',
    name: 'Dharmendra',
    role: 'super_admin',
    permissions: ['all'],
    defaultPassword: 'admin123'
  }
];

/**
 * Check if current user is an admin
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return false;
    }

    // Check if user matches any admin user
    const isAdmin = ADMIN_USERS.some(admin => {
      const emailMatch = admin.email && user.email && admin.email.toLowerCase() === user.email.toLowerCase();
      const phoneMatch = admin.phone && user.phone && admin.phone === user.phone;
      const firebaseMatch = user.id && admin.email && user.email === admin.email;
      return emailMatch || phoneMatch || firebaseMatch;
    });

    return isAdmin;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Get current user's admin info
 */
export async function getCurrentUserAdminInfo(): Promise<AdminUser | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return null;
    }

    // Find matching admin user
    const adminUser = ADMIN_USERS.find(admin => {
      const emailMatch = admin.email && user.email && admin.email.toLowerCase() === user.email.toLowerCase();
      const phoneMatch = admin.phone && user.phone && admin.phone === user.phone;
      const firebaseMatch = user.id && admin.email && user.email === admin.email;
      return emailMatch || phoneMatch || firebaseMatch;
    });

    return adminUser || null;
  } catch (error) {
    console.error('Error getting admin info:', error);
    return null;
  }
}

/**
 * Check if user has specific permission
 */
export async function hasPermission(permission: string): Promise<boolean> {
  try {
    const adminInfo = await getCurrentUserAdminInfo();
    
    if (!adminInfo) {
      return false;
    }

    const hasPermission = adminInfo.permissions.includes('all') || adminInfo.permissions.includes(permission);
    return hasPermission;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
}

/**
 * Check if user is super admin
 */
export async function isSuperAdmin(): Promise<boolean> {
  try {
    const adminInfo = await getCurrentUserAdminInfo();
    return adminInfo?.role === 'super_admin';
  } catch (error) {
    console.error('Error checking super admin status:', error);
    return false;
  }
}

/**
 * Get all admin users
 */
export function getAllAdminUsers(): AdminUser[] {
  return [...ADMIN_USERS];
}

/**
 * Get admin user by email
 */
export function getAdminUserByEmail(email: string): AdminUser | null {
  return ADMIN_USERS.find(admin => admin.email.toLowerCase() === email.toLowerCase()) || null;
}

/**
 * Get default password for admin user
 */
export function getAdminDefaultPassword(email: string): string | null {
  const adminUser = getAdminUserByEmail(email);
  return adminUser?.defaultPassword || null;
}

/**
 * Verify admin password
 */
export function verifyAdminPassword(email: string, password: string): boolean {
  const adminUser = getAdminUserByEmail(email);
  return adminUser?.defaultPassword === password;
}

/**
 * Add new admin user
 */
export function addAdminUser(newAdmin: Omit<AdminUser, 'defaultPassword'> & { defaultPassword?: string }): boolean {
  try {
    // Check if admin already exists
    if (getAdminUserByEmail(newAdmin.email)) {
      console.warn('Admin user already exists:', newAdmin.email);
      return false;
    }

    // Add to admin users list
    ADMIN_USERS.push({
      ...newAdmin,
      defaultPassword: newAdmin.defaultPassword || 'admin123'
    });

    console.log('Admin user added:', newAdmin.email);
    return true;
  } catch (error) {
    console.error('Error adding admin user:', error);
    return false;
  }
}

/**
 * Remove admin user
 */
export function removeAdminUser(email: string): boolean {
  try {
    const index = ADMIN_USERS.findIndex(admin => admin.email.toLowerCase() === email.toLowerCase());
    
    if (index === -1) {
      console.warn('Admin user not found:', email);
      return false;
    }

    ADMIN_USERS.splice(index, 1);
    console.log('Admin user removed:', email);
    return true;
  } catch (error) {
    console.error('Error removing admin user:', error);
    return false;
  }
}

/**
 * Get admin dashboard URL
 */
export function getAdminDashboardUrl(): string {
  return '/admin';
}

/**
 * Get admin users management URL
 */
export function getAdminUsersUrl(): string {
  return '/admin/users';
}

/**
 * Get admin activities URL
 */
export function getAdminActivitiesUrl(): string {
  return '/admin/activities';
}

/**
 * Validate admin credentials
 */
export function validateAdminCredentials(email: string, password: string): boolean {
  return verifyAdminPassword(email, password);
}
